import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import CONSTANTS from 'src/store/constants';

const clientSelector = ({ app }) => app.client;

/**
 * usePlatformConfig - creates platform configuration based on client platform selections
 *
 * @returns {object}
 */
export default function usePlatformConfig() {
  const client = useSelector(clientSelector);
  const { enableUpgradedPlatform, enableLegacyPlatform, showLegacyPlatformTags } = client ?? {};

  const config = useMemo(() => {
    const baseConfig = {
      canAction: (platform) =>
        baseConfig.canActionLegacyPlatform(platform) ||
        baseConfig.canActionUpgradedPlatform(platform) ||
        baseConfig.enableLegacyPlatform,
      canActionLegacyPlatform: (platform) => enableLegacyPlatform && baseConfig.isLegacyPlatform(platform),
      canActionUpgradedPlatform: (platform) => enableUpgradedPlatform && baseConfig.isUpgradedPlatform(platform),
      isDeprecatedPlatform: (platform) => CONSTANTS.PLATFORM.REFRESH_VERSIONS.DEPRECATED.includes(platform),
      isLegacyPlatform: (platform) => CONSTANTS.PLATFORM.REFRESH_VERSIONS.LEGACY === platform,
      isUpgradedPlatform: (platform) => CONSTANTS.PLATFORM.REFRESH_VERSIONS.UPGRADED === platform,
      getDeprecatedTag: (_) => null,
      getModels: (models) => models,
      includeRefresh: enableUpgradedPlatform || showLegacyPlatformTags,
      enableUpgradedPlatform,
      enableLegacyPlatform,
      showLegacyPlatformTags,
    };

    if (enableUpgradedPlatform && !enableLegacyPlatform) {
      baseConfig.getModels = (models) =>
        models.filter((model) => baseConfig.isUpgradedPlatform(model?.modelData?.metadataPayload?.refresh?.platform));
      baseConfig.getDeprecatedTag = (platform) =>
        showLegacyPlatformTags && !baseConfig.isUpgradedPlatform(platform) ? CONSTANTS.PLATFORM.TAG.DEPRECATED : null;
    } else if (enableUpgradedPlatform && enableLegacyPlatform) {
      baseConfig.getDeprecatedTag = (platform) => {
        if (showLegacyPlatformTags && !baseConfig.isUpgradedPlatform(platform)) {
          if (baseConfig.isLegacyPlatform(platform)) {
            return CONSTANTS.PLATFORM.TAG.LEGACY;
          }
          if (baseConfig.isDeprecatedPlatform(platform)) {
            return CONSTANTS.PLATFORM.TAG.DEPRECATED;
          }
          return CONSTANTS.PLATFORM.TAG.LEGACY;
        }
        return null;
      };
    }

    return baseConfig;
  }, [enableUpgradedPlatform, enableLegacyPlatform, showLegacyPlatformTags]);

  return config;
}
